import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { Eye, EyeOff, Upload, Check, User, Calendar, Lock, Sparkles } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SetupPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [tokenValid, setTokenValid] = useState(false);
    const [userData, setUserData] = useState(null);
    const [systemSettings, setSystemSettings] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [profilePreview, setProfilePreview] = useState(null);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
        birthday: '',
        profileImage: null
    });
    
    const [errors, setErrors] = useState({});
    const [birthdayPreset, setBirthdayPreset] = useState(false);

    useEffect(() => {
        // Fetch system settings for logo (public endpoint, no auth needed)
        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/system/public-settings`);
                setSystemSettings(response.data);
            } catch (error) {
                console.log('Using default settings');
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        const validateToken = async () => {
            if (!token) {
                setLoading(false);
                return;
            }
            
            try {
                // Validate token by checking if it exists
                const response = await axios.get(`${API_URL}/api/auth/validate-invite?token=${token}`);
                setTokenValid(true);
                setUserData(response.data);
                
                // If admin already set birthday, pre-fill it (normalize multiple formats to MM-DD)
                if (response.data.birthday) {
                    let raw = String(response.data.birthday).trim();
                    let normalized = '';
                    // Match patterns: "MM-DD", "DD.MM", "DD.MM.", "YYYY-MM-DD"
                    if (/^\d{2}-\d{2}$/.test(raw)) {
                        normalized = raw;
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                        const [, mm, dd] = raw.match(/^\d{4}-(\d{2})-(\d{2})$/);
                        normalized = `${mm}-${dd}`;
                    } else {
                        const m = raw.match(/^(\d{1,2})\.(\d{1,2})\.?$/);
                        if (m) {
                            const day = m[1].padStart(2, '0');
                            const month = m[2].padStart(2, '0');
                            normalized = `${month}-${day}`;
                        }
                    }
                    if (normalized) {
                        setFormData(prev => ({ ...prev, birthday: normalized }));
                        setBirthdayPreset(true);
                    }
                }
            } catch (error) {
                setTokenValid(false);
            } finally {
                setLoading(false);
            }
        };
        
        validateToken();
    }, [token]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.password) {
            newErrors.password = 'Passwort erforderlich';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Mindestens 8 Zeichen';
        }
        
        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwörter stimmen nicht überein';
        }
        
        // Birthday is only required if admin didn't set it (and never for a password reset)
        const isReset = userData?.purpose === 'reset';
        if (!birthdayPreset && !isReset) {
            const parts = (formData.birthday || '').split('-');
            const month = parts[0];
            const day = parts[1];
            if (!month && !day) {
                newErrors.birthday = 'Bitte Tag und Monat wählen';
            } else if (!month) {
                newErrors.birthday = 'Bitte Monat wählen';
            } else if (!day) {
                newErrors.birthday = 'Bitte Tag wählen';
            } else {
                const m = parseInt(month, 10);
                const d = parseInt(day, 10);
                const monthMax = { 1: 31, 2: 29, 3: 31, 4: 30, 5: 31, 6: 30, 7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31 };
                if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= (monthMax[m] || 31))) {
                    newErrors.birthday = 'Ungültiges Datum';
                }
            }
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Bild zu groß (max. 5MB)');
                return;
            }
            setFormData({ ...formData, profileImage: file });
            setProfilePreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;
        
        setSubmitting(true);
        
        try {
            // Setup account
            await axios.post(`${API_URL}/api/auth/setup`, {
                token,
                password: formData.password,
                birthday: formData.birthday || null
            });
            
            // Upload profile image if provided
            if (formData.profileImage && userData?.id) {
                const imageFormData = new FormData();
                imageFormData.append('file', formData.profileImage);
                
                // Login first to get token
                const loginRes = await axios.post(`${API_URL}/api/auth/login`, {
                    username: userData.username,
                    password: formData.password
                });
                
                await axios.post(
                    `${API_URL}/api/system/users/${userData.id}/profile-image`,
                    imageFormData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'Authorization': `Bearer ${loginRes.data.access_token}`
                        }
                    }
                );
            }
            
            toast.success('Konto erfolgreich eingerichtet! 🎉');
            navigate('/login');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler bei der Einrichtung');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!token || !tokenValid) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                >
                    <Card className="max-w-md shadow-floating">
                        <CardContent className="pt-8 pb-8 text-center">
                            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                                <span className="text-3xl">😕</span>
                            </div>
                            <h2 className="text-xl font-semibold mb-2">Ungültiger Link</h2>
                            <p className="text-muted-foreground mb-6">
                                Dieser Einladungslink ist ungültig oder abgelaufen.
                                Bitte kontaktiere deinen Administrator.
                            </p>
                            <Button onClick={() => navigate('/login')}>
                                Zum Login
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-primary/5 p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-lg"
            >
                {/* Header */}
                <div className="text-center mb-8">
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.1, type: 'spring', bounce: 0.5 }}
                        className={`inline-flex w-20 h-20 rounded-2xl items-center justify-center mb-4 shadow-floating overflow-hidden ${!systemSettings?.logo_url ? 'bg-primary' : ''}`}
                    >
                        {systemSettings?.logo_url ? (
                            <img src={systemSettings.logo_url} alt="Logo" className="w-full h-full object-contain" />
                        ) : (
                            <Sparkles className="w-8 h-8 text-white" />
                        )}
                    </motion.div>
                    <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {userData?.purpose === 'reset'
                            ? 'Neues Passwort festlegen'
                            : <>Willkommen bei {systemSettings?.company_name || 'AgencyOS'}! 🎉</>}
                    </h1>
                    <p className="text-muted-foreground mt-1">AgencyOS</p>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {userData?.purpose === 'reset'
                            ? <>Hallo {userData?.first_name}, wähle ein neues Passwort für dein Konto.</>
                            : <>Richte dein Konto ein, {userData?.first_name}</>}
                    </p>
                </div>

                <Card className="shadow-floating">
                    <CardContent className="pt-6">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Profile Image */}
                            <div className="flex flex-col items-center">
                                <Label className="mb-3 flex items-center gap-2">
                                    <User className="w-4 h-4" />
                                    Profilbild
                                </Label>
                                <div 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="relative cursor-pointer group"
                                >
                                    <Avatar className="w-24 h-24 border-4 border-background shadow-lg">
                                        {profilePreview ? (
                                            <AvatarImage src={profilePreview} />
                                        ) : null}
                                        <AvatarFallback className="text-2xl bg-primary text-white">
                                            {userData?.first_name?.[0]}{userData?.last_name?.[0]}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Upload className="w-6 h-6 text-white" />
                                    </div>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                                <p className="text-xs text-muted-foreground mt-2">
                                    Klicke zum Hochladen (optional)
                                </p>
                            </div>

                            {/* Birthday (nicht beim Passwort-Reset) */}
                            {userData?.purpose !== 'reset' && (
                            <div>
                                <Label className="flex items-center gap-2">
                                    <Calendar className="w-4 h-4" />
                                    Geburtstag {!birthdayPreset && '*'}
                                    {birthdayPreset && <span className="text-xs text-muted-foreground font-normal">(vom Admin eingetragen)</span>}
                                </Label>
                                {birthdayPreset ? (
                                    (() => {
                                        // Display preset birthday nicely
                                        const parts = (formData.birthday || '').split('-');
                                        let display = formData.birthday;
                                        if (parts.length === 2) {
                                            // Already MM-TT
                                            display = `${parts[1]}.${parts[0]}.`;
                                        }
                                        return (
                                            <Input
                                                value={display}
                                                className="mt-2 bg-muted/50"
                                                disabled
                                            />
                                        );
                                    })()
                                ) : (
                                    <div className="mt-2 grid grid-cols-2 gap-2" data-testid="birthday-picker">
                                        {(() => {
                                            const parts = (formData.birthday || '').split('-');
                                            const currentMonth = parts[0] || '';
                                            const currentDay = parts[1] || '';
                                            const setMonth = (m) => {
                                                setFormData({ ...formData, birthday: `${m}-${currentDay}` });
                                            };
                                            const setDay = (d) => {
                                                setFormData({ ...formData, birthday: `${currentMonth}-${d}` });
                                            };
                                            const months = [
                                                { v: '01', l: 'Januar' }, { v: '02', l: 'Februar' }, { v: '03', l: 'März' },
                                                { v: '04', l: 'April' }, { v: '05', l: 'Mai' }, { v: '06', l: 'Juni' },
                                                { v: '07', l: 'Juli' }, { v: '08', l: 'August' }, { v: '09', l: 'September' },
                                                { v: '10', l: 'Oktober' }, { v: '11', l: 'November' }, { v: '12', l: 'Dezember' },
                                            ];
                                            // Days depend on month for max value
                                            const monthMax = {
                                                '01': 31, '02': 29, '03': 31, '04': 30, '05': 31, '06': 30,
                                                '07': 31, '08': 31, '09': 30, '10': 31, '11': 30, '12': 31,
                                            };
                                            const maxDay = monthMax[currentMonth] || 31;
                                            const days = Array.from({ length: maxDay }, (_, i) => String(i + 1).padStart(2, '0'));
                                            return (
                                                <>
                                                    <Select value={currentDay} onValueChange={setDay}>
                                                        <SelectTrigger data-testid="birthday-day">
                                                            <SelectValue placeholder="Tag" />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-[260px]">
                                                            {days.map(d => (
                                                                <SelectItem key={d} value={d}>{parseInt(d, 10)}.</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <Select value={currentMonth} onValueChange={setMonth}>
                                                        <SelectTrigger data-testid="birthday-month">
                                                            <SelectValue placeholder="Monat" />
                                                        </SelectTrigger>
                                                        <SelectContent className="max-h-[260px]">
                                                            {months.map(m => (
                                                                <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </>
                                            );
                                        })()}
                                    </div>
                                )}
                                {errors.birthday && (
                                    <p className="text-xs text-destructive mt-1">{errors.birthday}</p>
                                )}
                                {!birthdayPreset && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                        🎂 Damit wir deinen Geburtstag feiern können!
                                    </p>
                                )}
                            </div>
                            )}

                            {/* Password */}
                            <div>
                                <Label className="flex items-center gap-2">
                                    <Lock className="w-4 h-4" />
                                    Passwort *
                                </Label>
                                <div className="relative mt-2">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="Mindestens 8 Zeichen"
                                        className={errors.password ? 'border-destructive pr-10' : 'pr-10'}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="text-xs text-destructive mt-1">{errors.password}</p>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <Label className="flex items-center gap-2">
                                    <Check className="w-4 h-4" />
                                    Passwort bestätigen *
                                </Label>
                                <div className="relative mt-2">
                                    <Input
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        placeholder="Passwort wiederholen"
                                        className={errors.confirmPassword ? 'border-destructive pr-10' : 'pr-10'}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="text-xs text-destructive mt-1">{errors.confirmPassword}</p>
                                )}
                            </div>

                            {/* Submit */}
                            <Button 
                                type="submit" 
                                className="w-full h-12 text-base"
                                disabled={submitting}
                            >
                                {submitting ? (
                                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <>
                                        <Sparkles className="w-4 h-4 mr-2" />
                                        Konto einrichten
                                    </>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                <p className="text-center text-sm text-muted-foreground mt-6">
                    Benutzername: <span className="font-medium text-foreground">{userData?.username}</span>
                </p>
            </motion.div>
        </div>
    );
};

export default SetupPage;
